import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  CONSENT_KEY,
  CONSENT_VERSION,
  CONSENT_DAYS,
  clearGaCookies,
} from "./store";

const ConsentContext = createContext(null);

function readStored() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed.analytics !== "boolean" ||
      typeof parsed.expires !== "number" ||
      !parsed.expires ||
      parsed.expires < Date.now() ||
      parsed.version !== CONSENT_VERSION
    ) {
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }
    return parsed;
  } catch (e) {
    try {
      localStorage.removeItem(CONSENT_KEY);
    } catch (e2) {
      /* ignorează */
    }
    return null;
  }
}

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(readStored);
  const [bannerOpen, setBannerOpen] = useState(() => !readStored());

  useEffect(() => {
    if (!consent) return;
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    } catch (e) {
      /* stocare indisponibilă — consimțământul rămâne doar în memorie */
    }
  }, [consent]);

  // Dacă utilizatorul a ales anterior „Doar necesare", browserul poate avea
  // cookie-uri GA rămase de la altă alegere; le curățăm la pornire.
  useEffect(() => {
    if (consent && consent.analytics === false) {
      clearGaCookies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      consent,
      bannerOpen,
      acceptAll() {
        setConsent({
          analytics: true,
          saved: Date.now(),
          expires: Date.now() + CONSENT_DAYS * 24 * 3600 * 1000,
          version: CONSENT_VERSION,
        });
        setBannerOpen(false);
      },
      acceptNecessary() {
        setConsent({
          analytics: false,
          saved: Date.now(),
          expires: Date.now() + CONSENT_DAYS * 24 * 3600 * 1000,
          version: CONSENT_VERSION,
        });
        setBannerOpen(false);
        // retragere/refuz: ștergem cookie-urile de analiză cunoscute
        clearGaCookies();
      },
      openBanner() {
        setBannerOpen(true);
      },
    }),
    [consent, bannerOpen]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  return useContext(ConsentContext);
}
